import React, { useState, useEffect, useRef } from "react";

export default function LazyCardWrapper({ children }) {
  const [isVisible, setIsVisible] = useState(false);
  const placeholderRef = useRef(null);

  useEffect(() => {
    // This observer stays connected for the life of the card. An earlier
    // version disconnected on first intersection to protect flip state, which
    // meant every card scrolled past stayed mounted forever: scrolling the
    // whole deck on a phone mounted all 453 at once and Chrome killed the tab
    // with "Aw, Snap". Flip state now lives above this component (see
    // `flippedCards` in App.js), so unmounting an offscreen card is free and
    // memory stays flat no matter how far the user scrolls.
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        // Roughly one screen of runway either side. Large enough that a card is
        // already mounted before it scrolls into view, small enough that a
        // single-column mobile layout holds only a handful at a time.
        rootMargin: "300px 0px",
      }
    );

    const currentRef = placeholderRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Card faces are absolutely positioned to fill this wrapper, so this height is
  // a hard ceiling on the back face: it must fit the answer plus the optional
  // `hook` memory aid and flag image (see FlashCard.js). h-72 was measured
  // against the longest answer+hook+image combination in studyGuideData.js —
  // shrink it and the busiest cards start scrolling instead of fitting.
  //
  // The height is fixed here rather than by the child, so swapping a card for
  // its placeholder never changes layout or shifts the scroll position.
  return (
    <div ref={placeholderRef} className="h-72">
      {isVisible ? (
        children
      ) : (
        // No pulse animation: with hundreds of these on screen the animations
        // are themselves compositor work, and a skeleton over an already-read
        // card reads as a loading failure rather than a placeholder.
        <div className="w-full h-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800" />
      )}
    </div>
  );
}
